import React from 'react';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import NorthIcon from '@mui/icons-material/North';
import SouthIcon from '@mui/icons-material/South';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import NewDashboardChart, { extractTopDriversList, extractTopCustomersList } from './NewDashboardChart';
import JobTable from './JobTable';
import { useNewDashboardLayout } from './useNewDashboardLayout';
import { useSelector } from 'react-redux';
import client from 'client';
import CircularProgress from '@mui/material/CircularProgress';
import OriginalJobLabel from 'app/main/loadz/components/OriginalJobLabel';

/** Path segment para el dropdown "Top 3 Drivers" → solo actualiza esa lista. */
const CALL_TYPE_TOP_DRIVERS = 'topDrivers';
/** Path segment para el dropdown "Top Customers" → solo actualiza esa lista. */
const CALL_TYPE_TOP_CUSTOMERS = 'topCustomers';

const ticketModalTableHeaderCellSx = {
  fontWeight: 600,
  backgroundColor: '#FFC107 !important',
  color: '#000000',
  py: 0.5,
  px: 1.5,
  border: 'none !important',
  borderWidth: '0 !important',
  borderStyle: 'none !important',
  borderRight: 'none !important',
  borderLeft: 'none !important',
  borderTop: 'none !important',
  borderBottom: 'none !important',
  outline: 'none',
  boxShadow: 'none !important',
  backgroundImage: 'none',
  '&.MuiTableCell-stickyHeader': {
    boxShadow: 'none !important',
    backgroundColor: '#FFC107 !important',
    border: 'none !important',
    borderBottom: 'none !important',
  },
};

/** Columnas del modal de tickets (ordenación por clic en encabezado). */
const TICKET_MODAL_COLUMNS = [
  { key: 'ticket', label: 'Ticket No.' },
  { key: 'qty', label: 'Qty' },
  { key: 'driver', label: 'Driver' },
  { key: 'truck', label: 'Truck No.' },
  { key: 'scale', label: 'Scale' },
  { key: 'dateTime', label: 'Date Time Created' },
  { key: 'dateTimeUpdate', label: 'Date Time Updated' },
];

function compareTicketModalRows(a, b, sortKey, dir) {
  const sign = dir === 'asc' ? 1 : -1;
  switch (sortKey) {
    case 'ticket': {
      const va = String(a?.ticket ?? '');
      const vb = String(b?.ticket ?? '');
      return va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }) * sign;
    }
    case 'qty':
      return (Number(a?.qty) - Number(b?.qty)) * sign;
    case 'driver':
    case 'truck':
    case 'scale': {
      const va = String(a?.[sortKey] ?? '');
      const vb = String(b?.[sortKey] ?? '');
      return va.localeCompare(vb, undefined, { sensitivity: 'base' }) * sign;
    }
    case 'dateTime':
    case 'dateTimeUpdate': {
      const ma = moment(a?.[sortKey]);
      const mb = moment(b?.[sortKey]);
      const va = ma.isValid() ? ma.valueOf() : 0;
      const vb = mb.isValid() ? mb.valueOf() : 0;
      if (va === vb) return 0;
      return va < vb ? -sign : sign;
    }
    default:
      return 0;
  }
}

/** Espacio reservado sobre la fila del new dashboard (toolbar Fuse + header + padding del dashboard). */
/* Menor offset = fila de 3 columnas más alta (más “largas” en vertical). */
const NEW_DASHBOARD_VIEWPORT_OFFSET_XS = 100;
const NEW_DASHBOARD_VIEWPORT_OFFSET_MD = 160;

/** Separación título (compname) → columnas; también en `rowMaxH`. */
const NEW_DASHBOARD_GAP_BELOW_TITLE_XS = 10;
const NEW_DASHBOARD_GAP_BELOW_TITLE_MD = 20;

/** `display:flex` en fila hace que la Card no estire al ancho de la celda; columna + 100% lo corrige. */
const newDashboardColumnGridItemSx = {
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  maxHeight: { md: '100%' },
};

const newDashboardColumnCardFullWidthSx = {
  width: '100%',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
};

function formatFinanceAmount(n) {
  return Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Primer string no vacío (nombres / API con distintas claves). */
function pickStr(...vals) {
  for (const v of vals) {
    if (v != null && String(v).trim() !== '') return String(v).trim();
  }
  return '';
}

function financeGrowthColor(value) {
  if (value > 0) return '#2e7d32';
  if (value < 0) return '#d32f2f';
  return 'rgba(0,0,0,0.5)';
}

function financeGrowthDirectionIconSx(band) {
  const fontSize = band === 'compact' ? 7 : band === 'expanded' ? 9 : 8;
  return {
    fontSize,
    color: 'inherit',
    overflow: 'visible',
    '& path': {
      stroke: 'currentColor',
      strokeWidth: 1.1,
      paintOrder: 'stroke fill',
    },
  };
}

/** Valor principal ($) + crecimiento a la derecha (verde / rojo). */
function FinanceMetricValueRow({ main, growth, band }) {
  const mainSx = {
    color: '#000000',
    lineHeight: 1,
    wordBreak: 'break-word',
    ...(band === 'compact' && { fontSize: '1rem' }),
    ...(band === 'reference' && { fontSize: '1.25rem' }),
    ...(band === 'expanded' && { fontSize: '1.4rem' }),
  };
  const showGrowth = growth != null && Number.isFinite(growth);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        flexWrap: 'nowrap',
        minWidth: 0,
        width: '100%',
      }}
    >
      <Typography
        className='font-bold'
        sx={{
          ...mainSx,
          flex: showGrowth ? '0 1 auto' : 1,
          minWidth: 0,
          textAlign: showGrowth ? 'right' : 'center',
          m: 0,
        }}
      >
        ${formatFinanceAmount(main)}
      </Typography>
      {showGrowth && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            flexShrink: 0,
            color: financeGrowthColor(growth),
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          {growth > 0 && <NorthIcon sx={financeGrowthDirectionIconSx(band)} />}
          {growth < 0 && <SouthIcon sx={financeGrowthDirectionIconSx(band)} />}
          <Typography
            component='span'
            sx={{
              fontWeight: 700,
              fontSize: band === 'compact' ? '0.8rem' : band === 'expanded' ? '1rem' : '0.9rem',
              m: 0,
              ml: '-2px',
            }}
          >
            {formatFinanceAmount(Math.abs(growth))}%
          </Typography>
        </Box>
      )}
    </div>
  );
}

/** Fila única de los 3 contadores Dispatch; `cqw` escala al ancho real de la columna 3. */
const dispatchCountersContainerSx = {
  width: '100%',
  minWidth: 0,
  containerType: 'inline-size',
};

const dispatchCountersRowSx = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: { xs: 0.25, sm: 0.5 },
  width: '100%',
  minWidth: 0,
  py: 0.5,
};

const dispatchCounterCellSx = {
  flex: '1 1 0',
  minWidth: 0,
  textAlign: 'center',
  px: { xs: 0, sm: 0.25 },
};

const dispatchCounterValueSx = {
  fontWeight: 'bold',
  color: '#FFC107',
  lineHeight: 1.1,
  /** `cqw` sigue el ancho de la columna; `vmin` evita tamaño 0 si el contenedor no aplica. */
  fontSize: 'clamp(10px, max(6.5cqw, 2.85vmin), 2.65rem)',
};

const dispatchCounterLabelSx = {
  color: '#000000',
  mt: '4px',
  lineHeight: 1.15,
  fontSize: 'clamp(8px, max(2.75cqw, 1.1vmin), 1.2rem)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

function NewDashboardView({ 
  compname, 
  refreshGraph, 
  refreshDispatchSection,
  comp_acct, 
  progress, 
  completed, 
  progressTickets, 
  completedTickets,
  getTicketData,
  getDriverEarnings,
  setProgress,
  setCompleted,
  setDelayed,
  setProgressTickets,
  setCompletedTickets,
  setDelayedTickets,
  setQtyProgress,
  setQtyCompleted,
  setQtyDelayed,
  refreshProgress,
  refreshCompleted,
  refreshDelayed,
  allTablesData,
  isDelayed,
}) {
  const history = useHistory();
  
  const [financialData, setFinancialData] = React.useState({
    totalEarnings: 0,
    materialSales: 0,
    gasCost: 0,
    commissions: 0,
    grossProfit: 0,
    outstandingTickets: 0,
    growth: {
      totalEarnings: null,
      materialSales: null,
      gasCost: null,
      commissions: null,
      grossProfit: null,
      outstandingTickets: null,
    },
  });
  
  const [dispatchData, setDispatchData] = React.useState({
    trucksInUse: 0,
    trucksAvailable: 0,
    driversAvailable: 0,
    totalTrucks: 0,
  });
  
  const [topDriversData, setTopDriversData] = React.useState([]);
  
  const [topCustomersData, setTopCustomersData] = React.useState([]);

  /**
   * Periodos independientes (default Weekly). Van en query en la petición `.../full` al activar
   * el switch "New Dashboard View". Los dropdowns de Dashboard / Top 3 Drivers / Top Customers disparan sus APIs.
   */
  const [chartSummaryPeriod, setChartSummaryPeriod] = React.useState('weekly');
  const [topDriversSummaryPeriod, setTopDriversSummaryPeriod] = React.useState('weekly');
  const [topCustomersSummaryPeriod, setTopCustomersSummaryPeriod] = React.useState('weekly');

  const user = useSelector((state) => state.loadz_auth?.AuthReducer?.user);
  const userId = user?.user_id;
  const compacctnum = user?.company?.compacctnum ?? comp_acct;
  const topDriversFetchGenRef = React.useRef(0);
  const [topDriversListLoading, setTopDriversListLoading] = React.useState(false);

  const fetchTopDriversForPeriod = React.useCallback(
    async (period) => {
      if (!userId || !compacctnum || !period) return;
      const gen = ++topDriversFetchGenRef.current;
      setTopDriversListLoading(true);
      try {
        const res = await client.get(
          `/jobs/new-dashboard-graph-summary/${userId}/${compacctnum}/${period}/${CALL_TYPE_TOP_DRIVERS}`
        );
        if (gen !== topDriversFetchGenRef.current) return;
        const driversList = extractTopDriversList(res.data);
        setTopDriversData(driversList);
      } catch (error) {
        console.error('Error fetching Top 3 Drivers summary:', error);
      } finally {
        if (gen === topDriversFetchGenRef.current) {
          setTopDriversListLoading(false);
        }
      }
    },
    [userId, compacctnum]
  );

  const handleTopDriversPeriodChange = React.useCallback(
    (event) => {
      const next = event.target.value;
      setTopDriversSummaryPeriod(next);
      fetchTopDriversForPeriod(next);
    },
    [fetchTopDriversForPeriod]
  );

  const topCustomersFetchGenRef = React.useRef(0);
  const [topCustomersListLoading, setTopCustomersListLoading] = React.useState(false);

  const fetchTopCustomersForPeriod = React.useCallback(
    async (period) => {
      if (!userId || !compacctnum || !period) return;
      const gen = ++topCustomersFetchGenRef.current;
      setTopCustomersListLoading(true);
      try {
        const res = await client.get(
          `/jobs/new-dashboard-graph-summary/${userId}/${compacctnum}/${period}/${CALL_TYPE_TOP_CUSTOMERS}`
        );
        if (gen !== topCustomersFetchGenRef.current) return;
        const customersList = extractTopCustomersList(res.data);
        setTopCustomersData(customersList);
      } catch (error) {
        console.error('Error fetching Top Customers summary:', error);
      } finally {
        if (gen === topCustomersFetchGenRef.current) {
          setTopCustomersListLoading(false);
        }
      }
    },
    [userId, compacctnum]
  );

  const handleTopCustomersPeriodChange = React.useCallback(
    (event) => {
      const next = event.target.value;
      setTopCustomersSummaryPeriod(next);
      fetchTopCustomersForPeriod(next);
    },
    [fetchTopCustomersForPeriod]
  );
  
  const [ticketModalOpen, setTicketModalOpen] = React.useState(false);
  const [selectedJobData, setSelectedJobData] = React.useState(null);
  const [ticketModalSort, setTicketModalSort] = React.useState({ key: null, dir: 'asc' });

  const sortedTicketModalRows = React.useMemo(() => {
    const list = selectedJobData?.tickets;
    if (!Array.isArray(list) || list.length === 0) return [];
    if (!ticketModalSort.key) return list;
    const next = [...list];
    next.sort((a, b) =>
      compareTicketModalRows(a, b, ticketModalSort.key, ticketModalSort.dir)
    );
    return next;
  }, [selectedJobData?.tickets, ticketModalSort.key, ticketModalSort.dir]);

  const handleTicketModalHeaderClick = React.useCallback((columnKey) => {
    setTicketModalSort((prev) => {
      if (prev.key === columnKey) {
        return { key: columnKey, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: columnKey, dir: 'asc' };
    });
  }, []);

  const handleOpenTicketModal = (jobData) => {
    setTicketModalSort({ key: null, dir: 'asc' });
    setSelectedJobData(jobData);
    setTicketModalOpen(true);
  };

  const handleCloseTicketModal = () => {
    setTicketModalOpen(false);
    setSelectedJobData(null);
    setTicketModalSort({ key: null, dir: 'asc' });
  };
  
  const [jobsFilter, setJobsFilter] = React.useState('inProgress');
  /** Restaurar el filtro tras F5 o remount; misma clave al cambiar. */
  React.useLayoutEffect(() => {
    if (!comp_acct) return;
    try {
      const s = sessionStorage.getItem(`nd-jobsFilter-${comp_acct}`);
      if (s === 'inProgress' || s === 'completed' || s === 'delayed') {
        setJobsFilter(s);
      }
    } catch (e) {
      // ignore
    }
  }, [comp_acct]);
  
  const jobTypes = {
    progress: 0,
    completed: 1,
    delayed: 2,
  };
  
  const handleJobsFilterChange = (event) => {
    const v = event.target.value;
    setJobsFilter(v);
    if (comp_acct) {
      try {
        sessionStorage.setItem(`nd-jobsFilter-${comp_acct}`, v);
      } catch (e) {
        // ignore
      }
    }
  };
  
  // Determinar qué jobType y jobStatus usar según el filtro
  const getCurrentJobConfig = () => {
    switch(jobsFilter) {
      case 'inProgress':
        return {
          jobType: jobTypes.progress,
          jobStatus: 'Progress',
          setJobCount: setProgress,
          ticketCount: setProgressTickets,
          setQuantity: setQtyProgress,
          refreshTable: refreshProgress,
          initialData: allTablesData?.progress,
        };
      case 'completed':
        return {
          jobType: jobTypes.completed,
          jobStatus: 'Completed',
          setJobCount: setCompleted,
          ticketCount: setCompletedTickets,
          setQuantity: setQtyCompleted,
          refreshTable: refreshCompleted,
          initialData: allTablesData?.completed,
        };
      case 'delayed':
        return {
          jobType: jobTypes.delayed,
          jobStatus: 'Delayed',
          setJobCount: setDelayed,
          ticketCount: setDelayedTickets,
          setQuantity: setQtyDelayed,
          refreshTable: refreshDelayed,
          initialData: allTablesData?.delayed,
          isDelayed: isDelayed,
        };
      default:
        return {
          jobType: jobTypes.progress,
          jobStatus: 'Progress',
          setJobCount: setProgress,
          ticketCount: setProgressTickets,
          setQuantity: setQtyProgress,
          refreshTable: refreshProgress,
          initialData: allTablesData?.progress,
        };
    }
  };
  
  const jobConfig = getCurrentJobConfig();
  const { band } = useNewDashboardLayout();
  const theme = useTheme();
  const isXlUp = useMediaQuery(theme.breakpoints.up('xl'));

  /**
   * Altura de Dispatch: % de la columna + tope en px.
   * En `xl` (≥1536px) la tarjeta usa el doble de la base, luego reducido en 1/3 (factor 2/3 sobre ese doble).
   * Valores con +10% de largo respecto a la base.
   */
  const dispatchCardSizeSx = React.useMemo(() => {
    if (band === 'compact') {
      return { height: 'auto', maxHeight: { xs: 352, sm: 374 } };
    }
    if (band === 'expanded') {
      return isXlUp
        ? { height: '46.94%', maxHeight: 586 }
        : { height: '35.2%', maxHeight: 440 };
    }
    return isXlUp
      ? { height: '52.8%', maxHeight: 498 }
      : { height: '39.6%', maxHeight: 374 };
  }, [band, isXlUp]);

  const chartCardPad = band === 'compact' ? 14 : band === 'expanded' ? 26 : 20;
  const legendFontPx = band === 'compact' ? 9 : band === 'expanded' ? 8 : 7;
  const financeFlex = band !== 'reference';

  const rowMaxH = {
    xs: `calc(100dvh - ${NEW_DASHBOARD_VIEWPORT_OFFSET_XS + NEW_DASHBOARD_GAP_BELOW_TITLE_XS}px)`,
    md: `calc(100dvh - ${NEW_DASHBOARD_VIEWPORT_OFFSET_MD + NEW_DASHBOARD_GAP_BELOW_TITLE_MD}px)`,
  };

  return (
    <>
      <Grid
        item
        xs={12}
        sx={{
          mt: `${NEW_DASHBOARD_GAP_BELOW_TITLE_XS}px`,
          '@media (min-width: 900px)': {
            mt: `${NEW_DASHBOARD_GAP_BELOW_TITLE_MD}px`,
          },
          minHeight: 0,
          maxHeight: rowMaxH,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            alignItems: 'stretch',
            minHeight: 0,
            maxHeight: rowMaxH,
            height: { xs: rowMaxH.xs, md: rowMaxH.md },
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            overflowY: { xs: 'auto', md: 'hidden' },
            overflowX: 'hidden',
          }}
        >
      <Grid item xs={12} md={4} sx={newDashboardColumnGridItemSx}>
        <Card
          sx={{
            ...newDashboardColumnCardFullWidthSx,
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            height: { xs: 'auto', md: '100%' },
            maxHeight: { md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'visible',
            flex: { md: 1 },
          }}
        >
          <CardContent
            sx={{
              flex: { md: 1 },
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              p: `${chartCardPad}px`,
              overflow: 'visible',
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <NewDashboardChart
              refreshGraph={refreshGraph}
              refreshDispatchSection={refreshDispatchSection}
              compacct={comp_acct}
              onFinancialDataUpdate={setFinancialData}
              onDispatchDataUpdate={setDispatchData}
              onTopDriversDataUpdate={setTopDriversData}
              onTopCustomersDataUpdate={setTopCustomersData}
              layoutBand={band}
              chartPeriod={chartSummaryPeriod}
              onChartPeriodChange={setChartSummaryPeriod}
              topDriversPeriod={topDriversSummaryPeriod}
              topCustomersPeriod={topCustomersSummaryPeriod}
            />
            
            {/* Custom Legend */}
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '6px', 
              justifyContent: 'center',
              marginTop: '8px',
              marginBottom: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <div style={{ width: '6px', height: '6px', backgroundColor: '#008FFB', borderRadius: '1px' }}></div>
                <Typography style={{ fontSize: `${legendFontPx}px`, color: '#333' }}>Hauling Sales</Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <div style={{ width: '6px', height: '6px', backgroundColor: '#00E396', borderRadius: '1px' }}></div>
                <Typography style={{ fontSize: `${legendFontPx}px`, color: '#333' }}>Material Sales</Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <div style={{ width: '6px', height: '6px', backgroundColor: '#FEB019', borderRadius: '1px' }}></div>
                <Typography style={{ fontSize: `${legendFontPx}px`, color: '#333' }}>Wait Time Charges</Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <div style={{ width: '6px', height: '6px', backgroundColor: '#FF4560', borderRadius: '1px' }}></div>
                <Typography style={{ fontSize: `${legendFontPx}px`, color: '#333' }}>Fuel Costs</Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <div style={{ width: '6px', height: '6px', backgroundColor: '#775DD0', borderRadius: '1px' }}></div>
                <Typography style={{ fontSize: `${legendFontPx}px`, color: '#333' }}>Employee Commissions</Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <div style={{ width: '6px', height: '6px', backgroundColor: '#3F51B5', borderRadius: '1px' }}></div>
                <Typography style={{ fontSize: `${legendFontPx}px`, color: '#333' }}>Owner Operator Commissions</Typography>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                <div style={{ width: '6px', height: '6px', backgroundColor: '#D5EB33', borderRadius: '1px' }}></div>
                <Typography style={{ fontSize: `${legendFontPx}px`, color: '#333' }}>Fuel/Lube</Typography>
              </div>
            </div>
            
            <div
              className='flex gap-3 mt-4'
              style={{
                position: 'relative',
                flexWrap: financeFlex ? 'wrap' : 'nowrap',
                paddingTop: '12px',
              }}
            >
              <Paper
                className={financeFlex ? 'rounded-lg' : 'w-1/2 rounded-lg'}
                sx={
                  financeFlex
                    ? {
                        flex: '1 1 calc(50% - 12px)',
                        minWidth: 0,
                        backgroundColor: '#FFFFFF',
                        p: band === 'expanded' ? 1.75 : 1.5,
                      }
                    : { backgroundColor: '#FFFFFF', p: 1.5 }
                }
              >
                <div className='text-center'>
                  <Typography className='text-8 font-Regular' style={{ color: '#000000', marginBottom: '2px', lineHeight: 1 }}>In-Progress</Typography>
                  <Typography
                    className={band === 'reference' ? 'text-48 font-bold' : 'font-bold'}
                    sx={{
                      color: '#000000',
                      lineHeight: 1,
                      ...(band === 'compact' && { fontSize: '2rem' }),
                      ...(band === 'expanded' && { fontSize: '3.25rem' }),
                    }}
                  >
                    {progress || 0}
                  </Typography>
                </div>
              </Paper>

              <div
                style={{
                  width: '2px',
                  backgroundColor: '#000000',
                  alignSelf: 'stretch',
                  flexShrink: 0,
                }}
              />

              <Paper
                className={financeFlex ? 'rounded-lg' : 'w-1/2 rounded-lg'}
                sx={
                  financeFlex
                    ? {
                        flex: '1 1 calc(50% - 12px)',
                        minWidth: 0,
                        backgroundColor: '#FFFFFF',
                        p: band === 'expanded' ? 1.75 : 1.5,
                      }
                    : { backgroundColor: '#FFFFFF', p: 1.5 }
                }
              >
                <div className='text-center'>
                  <Typography className='text-8 font-Regular' style={{ color: '#000000', marginBottom: '2px', lineHeight: 1 }}>Completed</Typography>
                  <Typography
                    className={band === 'reference' ? 'text-48 font-bold' : 'font-bold'}
                    sx={{
                      color: '#000000',
                      lineHeight: 1,
                      ...(band === 'compact' && { fontSize: '2rem' }),
                      ...(band === 'expanded' && { fontSize: '3.25rem' }),
                    }}
                  >
                    {completed || 0}
                  </Typography>
                </div>
              </Paper>
            </div>
            
            <Typography
              className='font-Regular'
              sx={{
                fontWeight: 600,
                mt: '6px',
                lineHeight: 1.2,
                fontSize:
                  band === 'compact' ? '1.2rem' : band === 'expanded' ? '1.75rem' : '1.5rem',
              }}
            >
              Finance
            </Typography>
            
            <div className='flex gap-3 mt-4' style={{ position: 'relative', flexWrap: financeFlex ? 'wrap' : 'nowrap' }}>
              <Paper
                className={financeFlex ? 'rounded-lg' : 'w-1/2 rounded-lg'}
                sx={
                  financeFlex
                    ? {
                        flex: '1 1 calc(50% - 12px)',
                        minWidth: 0,
                        backgroundColor: '#F5F5F5',
                        py: band === 'expanded' ? 1.75 : 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        mr: '3px',
                        mb: '3px',
                      }
                    : {
                        backgroundColor: '#F5F5F5',
                        py: 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        mr: '3px',
                        mb: '3px',
                      }
                }
              >
                <div className='text-center' style={{ minWidth: 0 }}>
                  <Typography className='text-12 font-Regular' style={{ color: '#000000', marginBottom: '2px', lineHeight: 1 }}>Total Earnings</Typography>
                  <FinanceMetricValueRow
                    main={financialData.totalEarnings}
                    growth={financialData.growth?.totalEarnings}
                    band={band}
                  />
                </div>
              </Paper>

              <Paper
                className={financeFlex ? 'rounded-lg' : 'w-1/2 rounded-lg'}
                sx={
                  financeFlex
                    ? {
                        flex: '1 1 calc(50% - 12px)',
                        minWidth: 0,
                        backgroundColor: '#F5F5F5',
                        py: band === 'expanded' ? 1.75 : 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        ml: '3px',
                        mb: '3px',
                      }
                    : {
                        backgroundColor: '#F5F5F5',
                        py: 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        ml: '3px',
                        mb: '3px',
                      }
                }
              >
                <div className='text-center' style={{ minWidth: 0 }}>
                  <Typography className='text-12 font-Regular' style={{ color: '#000000', marginBottom: '2px', lineHeight: 1 }}>Material Sales</Typography>
                  <FinanceMetricValueRow
                    main={financialData.materialSales}
                    growth={financialData.growth?.materialSales}
                    band={band}
                  />
                </div>
              </Paper>
            </div>

            <div className='flex gap-3 mt-3' style={{ position: 'relative', flexWrap: financeFlex ? 'wrap' : 'nowrap' }}>
              <Paper
                className={financeFlex ? 'rounded-lg' : 'w-1/2 rounded-lg'}
                sx={
                  financeFlex
                    ? {
                        flex: '1 1 calc(50% - 12px)',
                        minWidth: 0,
                        backgroundColor: '#F5F5F5',
                        py: band === 'expanded' ? 1.75 : 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        mr: '3px',
                        mt: '3px',
                        mb: '3px',
                      }
                    : {
                        backgroundColor: '#F5F5F5',
                        py: 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        mr: '3px',
                        mt: '3px',
                        mb: '3px',
                      }
                }
              >
                <div className='text-center' style={{ minWidth: 0 }}>
                  <Typography className='text-12 font-Regular' style={{ color: '#000000', marginBottom: '2px', lineHeight: 1 }}>Gas Cost</Typography>
                  <FinanceMetricValueRow
                    main={financialData.gasCost}
                    growth={financialData.growth?.gasCost}
                    band={band}
                  />
                </div>
              </Paper>

              <Paper
                className={financeFlex ? 'rounded-lg' : 'w-1/2 rounded-lg'}
                sx={
                  financeFlex
                    ? {
                        flex: '1 1 calc(50% - 12px)',
                        minWidth: 0,
                        backgroundColor: '#F5F5F5',
                        py: band === 'expanded' ? 1.75 : 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        ml: '3px',
                        mt: '3px',
                        mb: '3px',
                      }
                    : {
                        backgroundColor: '#F5F5F5',
                        py: 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        ml: '3px',
                        mt: '3px',
                        mb: '3px',
                      }
                }
              >
                <div className='text-center' style={{ minWidth: 0 }}>
                  <Typography className='text-12 font-Regular' style={{ color: '#000000', marginBottom: '2px', lineHeight: 1 }}>Commissions</Typography>
                  <FinanceMetricValueRow
                    main={financialData.commissions}
                    growth={financialData.growth?.commissions}
                    band={band}
                  />
                </div>
              </Paper>
            </div>

            <div className='flex gap-3 mt-3' style={{ position: 'relative', flexWrap: financeFlex ? 'wrap' : 'nowrap' }}>
              <Paper
                className={financeFlex ? 'rounded-lg' : 'w-1/2 rounded-lg'}
                sx={
                  financeFlex
                    ? {
                        flex: '1 1 calc(50% - 12px)',
                        minWidth: 0,
                        backgroundColor: '#F5F5F5',
                        py: band === 'expanded' ? 1.75 : 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        mr: '3px',
                        mt: '3px',
                      }
                    : {
                        backgroundColor: '#F5F5F5',
                        py: 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        mr: '3px',
                        mt: '3px',
                      }
                }
              >
                <div className='text-center' style={{ minWidth: 0 }}>
                  <Typography className='text-12 font-Regular' style={{ color: '#000000', marginBottom: '2px', lineHeight: 1 }}>Gross Profit</Typography>
                  <FinanceMetricValueRow
                    main={financialData.grossProfit}
                    growth={financialData.growth?.grossProfit}
                    band={band}
                  />
                </div>
              </Paper>

              <Paper
                className={financeFlex ? 'rounded-lg' : 'w-1/2 rounded-lg'}
                sx={
                  financeFlex
                    ? {
                        flex: '1 1 calc(50% - 12px)',
                        minWidth: 0,
                        backgroundColor: '#F5F5F5',
                        py: band === 'expanded' ? 1.75 : 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        ml: '3px',
                        mt: '3px',
                      }
                    : {
                        backgroundColor: '#F5F5F5',
                        py: 1.5,
                        px: 0.75,
                        borderRadius: '20px',
                        ml: '3px',
                        mt: '3px',
                      }
                }
              >
                <div className='text-center' style={{ minWidth: 0 }}>
                  <Typography className='text-12 font-Regular' style={{ color: '#000000', marginBottom: '2px', lineHeight: 1 }}>In Progress Tickets</Typography>
                  <FinanceMetricValueRow
                    main={financialData.outstandingTickets}
                    growth={financialData.growth?.outstandingTickets}
                    band={band}
                  />
                </div>
              </Paper>
            </div>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4} sx={newDashboardColumnGridItemSx}>
        <Card
          sx={{
            ...newDashboardColumnCardFullWidthSx,
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            height: { xs: 'auto', md: '100%' },
            maxHeight: { md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            flex: { md: 1 },
          }}
        >
          <CardHeader 
            title={
              <Typography variant='h6' style={{ color: '#000000', fontWeight: 600 }}>
                Jobs
              </Typography>
            } 
            subheader='' 
            sx={{ paddingBottom: '0px', flexShrink: 0 }}
          />
          <CardContent
            sx={{
              paddingTop: 0,
              flex: { md: 1 },
              minHeight: 0,
              display: { md: 'flex' },
              flexDirection: { md: 'column' },
              width: '100%',
              minWidth: 0,
              boxSizing: 'border-box',
            }}
          >
            <JobTable
              getTicketData={getTicketData}
              getDriverEarnings={getDriverEarnings}
              setJobCount={jobConfig.setJobCount}
              refreshTable={jobConfig.refreshTable}
              ticketCount={jobConfig.ticketCount}
              setQuantity={jobConfig.setQuantity}
              compacct={comp_acct}
              jobType={jobConfig.jobType}
              jobStatus={jobConfig.jobStatus}
              initialData={jobConfig.initialData}
              waitForInitialData={!allTablesData}
              isDelayed={jobConfig.isDelayed}
              compactView={true}
              compactFillParent
              layoutBand={band}
              showHeader={true}
              headerTitle="Latest"
              filterValue={jobsFilter}
              onFilterChange={handleJobsFilterChange}
              onJobClick={handleOpenTicketModal}
              dashboardTableRefreshGroup={{
                progress: refreshProgress,
                completed: refreshCompleted,
                delayed: refreshDelayed,
              }}
            />
          </CardContent>
        </Card>
      </Grid>

      <Grid
        item
        xs={12}
        md={4}
        sx={{
          ...newDashboardColumnGridItemSx,
          gap: 1.5,
          height: { xs: 'auto', md: '100%' },
          alignSelf: 'stretch',
          overflow: 'hidden',
        }}
      >
        <Card
          sx={{
            ...newDashboardColumnCardFullWidthSx,
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            flexShrink: 0,
            minHeight: 0,
            ...dispatchCardSizeSx,
          }}
        >
          <CardHeader 
            title={
              <Typography variant='h6' style={{ color: '#000000', fontWeight: 600 }}>
                Dispatch
              </Typography>
            } 
            subheader='' 
            sx={{ paddingBottom: '0px', flexShrink: 0 }}
          />
          <CardContent
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              paddingTop: 0,
              paddingBottom: '6px',
              '&:last-child': { paddingBottom: '6px' },
            }}
          >
            <Box sx={{ ...dispatchCountersContainerSx, flexShrink: 0 }}>
              <Box sx={dispatchCountersRowSx}>
                <Box sx={dispatchCounterCellSx}>
                  <Typography sx={dispatchCounterValueSx}>
                    {dispatchData.trucksInUse}
                  </Typography>
                  <Typography sx={dispatchCounterLabelSx} title='Trucks In Use'>
                    Trucks In Use
                  </Typography>
                </Box>
                <Box sx={dispatchCounterCellSx}>
                  <Typography sx={dispatchCounterValueSx}>
                    {dispatchData.trucksAvailable}
                  </Typography>
                  <Typography sx={dispatchCounterLabelSx} title='Trucks Available'>
                    Trucks Available
                  </Typography>
                </Box>
                <Box sx={dispatchCounterCellSx}>
                  <Typography sx={dispatchCounterValueSx}>
                    {dispatchData.driversAvailable}
                  </Typography>
                  <Typography sx={dispatchCounterLabelSx} title='Available Drivers'>
                    Available Drivers
                  </Typography>
                </Box>
              </Box>
            </Box>
            
            {/* Progress Bar for Trucks In Use */}
            <div style={{ 
              padding: '0 0 4px 0',
              flexShrink: 0,
            }}>
              {/* Progress Bar */}
              <div style={{ 
                height: '32px',
                backgroundColor: '#000000',
                borderRadius: '16px',
                position: 'relative',
                width: '100%',
                border: '2px solid #FFFFFF',
                boxSizing: 'border-box'
              }}>
                {/* Yellow Progress */}
                <div style={{
                  height: '100%',
                  width: `${dispatchData.totalTrucks > 0 ? (dispatchData.trucksInUse / dispatchData.totalTrucks * 100) : 0}%`,
                  backgroundColor: '#FFC107',
                  transition: 'width 0.3s ease',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  borderRadius: '14px'
                }} />
                
                {/* Labels Container */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0 12px',
                  zIndex: 1
                }}>
                  {/* Percentage */}
                  <Typography style={{ 
                    fontSize: '14px', 
                    fontWeight: 'bold', 
                    color: '#FFFFFF'
                  }}>
                    {dispatchData.totalTrucks > 0 ? Math.round((dispatchData.trucksInUse / dispatchData.totalTrucks * 100)) : 0}%
                  </Typography>
                  
                  {/* Label */}
                  <Typography style={{ 
                    fontSize: '14px', 
                    color: '#FFFFFF',
                    whiteSpace: 'nowrap'
                  }}>
                    Trucks In Use
                  </Typography>
                </div>
              </div>
            </div>
            
            {/* Top 3 Drivers: título + periodo */}
            <div
              className='flex justify-between items-center'
              style={{
                gap: 8,
                flexShrink: 0,
                paddingTop: 8,
                paddingBottom: 8,
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#000000',
                  flexShrink: 0,
                }}
              >
                Top 3 Drivers
              </Typography>
              <FormControl
                size='extra-small'
                sx={{
                  flexShrink: 0,
                  '& > :first-child': {
                    paddingBottom: '3px !important',
                    paddingTop: '3px !important',
                  },
                }}
              >
                <Select
                  value={topDriversSummaryPeriod}
                  onChange={handleTopDriversPeriodChange}
                  sx={{
                    minWidth: 120,
                    borderRadius: '50px',
                    fontSize: '1.0rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#edca33',
                      borderRadius: '50px',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#edca33',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#edca33',
                    },
                    '& .MuiSelect-select': {
                      paddingBottom: '0px',
                      paddingTop: '0px',
                      paddingLeft: '0px',
                      paddingRight: '16px !important',
                    },
                  }}
                >
                  <MenuItem value='weekly'>Weekly</MenuItem>
                  <MenuItem value='monthly'>Monthly</MenuItem>
                  <MenuItem value='quarterly'>Quarterly</MenuItem>
                  <MenuItem value='yearly'>Yearly</MenuItem>
                </Select>
              </FormControl>
            </div>
            
            {/* Solo esta lista hace scroll dentro de la tarjeta Dispatch */}
            <div
              style={{
                flex: '1 1 0',
                minHeight: 0,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                position: 'relative',
              }}
            >
              {topDriversListLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                    zIndex: 2,
                  }}
                >
                  <CircularProgress sx={{ color: '#edca33' }} size={28} />
                </Box>
              )}
              {!topDriversListLoading && !topDriversData?.length ? (
                <Typography
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.75rem',
                    color: '#666',
                    textAlign: 'center',
                    padding: '24px 16px',
                    minHeight: 0,
                  }}
                >
                  No Reported Activity
                </Typography>
              ) : topDriversData?.length ? (
              topDriversData.map((driver, index) => {
                const period = Array.isArray(driver.current_period)
                  ? driver.current_period[0]
                  : driver.current_period;
                const first = pickStr(
                  driver.user_fname,
                  period?.user_fname,
                  period?.first_name,
                  period?.fname,
                  driver.first_name,
                  driver.fname
                );
                const last = pickStr(
                  driver.user_lname,
                  period?.user_lname,
                  period?.last_name,
                  period?.lname,
                  driver.last_name,
                  driver.lname
                );
                let displayName = [first, last].filter(Boolean).join(' ').trim();
                if (!displayName) {
                  displayName = pickStr(
                    driver.full_name,
                    period?.full_name,
                    driver.name,
                    period?.name,
                    driver.driver_name,
                    period?.driver_name
                  );
                }
                if (!displayName) displayName = '—';

                const initials =
                  displayName !== '—'
                    ? displayName
                        .split(/\s+/)
                        .filter(Boolean)
                        .map((w) => w[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase()
                    : '?';

                const rawGrowth = driver.growth;
                const growthObj = Array.isArray(rawGrowth) ? rawGrowth[0] : rawGrowth;
                const growthRevenueVal = Number.isFinite(driver.growthRevenue)
                  ? driver.growthRevenue
                  : parseFloat(growthObj?.revenue);
                const showGrowthRevenue = Number.isFinite(growthRevenueVal);

                return (
                <div 
                  key={driver.dr_user_id || index}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '4px 0',
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flex: '1 1 0%',
                      minWidth: 0,
                    }}
                  >
                    <Avatar
                      className='flex-shrink-0'
                      alt={displayName === '—' ? 'Driver' : displayName}
                      src={driver.driverImageLocation || period?.driverImageLocation || driver.avatar || undefined}
                      sx={{
                        color: '#000000',
                        width: '32px',
                        height: '32px',
                        fontSize: '0.75rem',
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Typography
                      style={{
                        fontSize: '12px',
                        color: '#000000',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={displayName}
                    >
                      {displayName}
                    </Typography>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      flexShrink: 0,
                      gap: 2,
                      textAlign: 'right',
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: '12px',
                        color: '#000000',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span style={{ fontWeight: 'bold' }}>Sales Volume:</span>{' '}
                      ${parseFloat(driver.total_revenue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                    {showGrowthRevenue && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0,
                          flexShrink: 0,
                          color: financeGrowthColor(growthRevenueVal),
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {growthRevenueVal > 0 && (
                          <NorthIcon sx={financeGrowthDirectionIconSx(band)} />
                        )}
                        {growthRevenueVal < 0 && (
                          <SouthIcon sx={financeGrowthDirectionIconSx(band)} />
                        )}
                        <Typography
                          component='span'
                          sx={{
                            fontWeight: 700,
                            fontSize: '11px',
                            m: 0,
                            ml: growthRevenueVal !== 0 ? '-2px' : 0,
                          }}
                        >
                          {formatFinanceAmount(Math.abs(growthRevenueVal))}%
                        </Typography>
                      </Box>
                    )}
                  </div>
                </div>
                );
              })
              ) : null}
            </div>
          </CardContent>
        </Card>
        
        <Card
          sx={{
            ...newDashboardColumnCardFullWidthSx,
            backgroundColor: '#FFFFFF',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            flex: { md: 1 },
            minHeight: { xs: 200, md: 0 },
            overflow: 'hidden',
          }}
        >
          <CardHeader
            disableTypography
            title={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  width: '100%',
                  minWidth: 0,
                }}
              >
                <Typography variant='h6' style={{ color: '#000000', fontWeight: 600 }}>
                  Top Customers
                </Typography>
                <FormControl
                  size='extra-small'
                  sx={{
                    flexShrink: 0,
                    '& > :first-child': {
                      paddingBottom: '3px !important',
                      paddingTop: '3px !important',
                    },
                  }}
                >
                  <Select
                    value={topCustomersSummaryPeriod}
                    onChange={handleTopCustomersPeriodChange}
                    sx={{
                      minWidth: 120,
                      borderRadius: '50px',
                      fontSize: '1.0rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#edca33',
                        borderRadius: '50px',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#edca33',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#edca33',
                      },
                      '& .MuiSelect-select': {
                        paddingBottom: '0px',
                        paddingTop: '0px',
                        paddingLeft: '0px',
                        paddingRight: '16px !important',
                      },
                    }}
                  >
                    <MenuItem value='weekly'>Weekly</MenuItem>
                    <MenuItem value='monthly'>Monthly</MenuItem>
                    <MenuItem value='quarterly'>Quarterly</MenuItem>
                    <MenuItem value='yearly'>Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            }
            subheader=''
            sx={{ paddingBottom: '0px', flexShrink: 0 }}
          />
          <CardContent
            sx={{
              paddingTop: '0px',
              paddingBottom: '16px !important',
              flex: 1,
              minHeight: 0,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Top Customers List */}
            <div style={{ 
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              position: 'relative',
              minHeight: 0,
            }}>
              {topCustomersListLoading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.75)',
                    zIndex: 2,
                  }}
                >
                  <CircularProgress sx={{ color: '#edca33' }} size={28} />
                </Box>
              )}
              {!topCustomersListLoading && !topCustomersData?.length ? (
                <Typography
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.75rem',
                    color: '#666',
                    textAlign: 'center',
                    padding: '24px 16px',
                    minHeight: 0,
                  }}
                >
                  No Reported Activity
                </Typography>
              ) : topCustomersData?.length ? (
              topCustomersData.map((customer, index) => {
                const period = Array.isArray(customer.current_period)
                  ? customer.current_period[0]
                  : customer.current_period;
                const displayName =
                  pickStr(
                    customer.customer_name,
                    customer.name,
                    period?.customer_name,
                    period?.name
                  ) || 'N/A';
                const revenue = parseFloat(
                  period?.total_revenue ?? customer.total_revenue ?? customer.revenue ?? 0
                );

                const rawGrowth = customer.growth;
                const growthObj = Array.isArray(rawGrowth) ? rawGrowth[0] : rawGrowth;
                const growthRevenueVal = Number.isFinite(customer.growthRevenue)
                  ? customer.growthRevenue
                  : parseFloat(growthObj?.revenue);
                const showGrowthRevenue = Number.isFinite(growthRevenueVal);

                const initials =
                  displayName !== 'N/A'
                    ? displayName
                        .split(/\s+/)
                        .filter(Boolean)
                        .map((w) => w[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase()
                    : '?';

                const customerAvatarSrc = pickStr(
                  customer.customerImageLocation,
                  period?.customerImageLocation,
                  customer.companyLogo,
                  period?.companyLogo,
                  customer.logo,
                  period?.logo,
                  customer.avatar,
                  period?.avatar
                );

                return (
                <div 
                  key={customer.client_id || index}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 8,
                    padding: '8px 12px',
                    backgroundColor: '#F5F5F5',
                    borderRadius: '8px',
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      flex: '1 1 0%',
                      minWidth: 0,
                    }}
                  >
                    <Avatar
                      className='flex-shrink-0'
                      alt={displayName === 'N/A' ? 'Customer' : displayName}
                      src={customerAvatarSrc || undefined}
                      sx={{
                        color: '#000000',
                        width: '32px',
                        height: '32px',
                        fontSize: '0.75rem',
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Typography
                      style={{
                        fontSize: '11px',
                        color: '#000000',
                        fontWeight: 500,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                      title={displayName}
                    >
                      {displayName}
                    </Typography>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      flexShrink: 0,
                      gap: 2,
                      textAlign: 'right',
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: '11px',
                        color: '#000000',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span style={{ fontWeight: 'bold' }}>Sales Volume:</span>{' '}
                      ${revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                    {showGrowthRevenue && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0,
                          flexShrink: 0,
                          color: financeGrowthColor(growthRevenueVal),
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {growthRevenueVal > 0 && (
                          <NorthIcon sx={financeGrowthDirectionIconSx(band)} />
                        )}
                        {growthRevenueVal < 0 && (
                          <SouthIcon sx={financeGrowthDirectionIconSx(band)} />
                        )}
                        <Typography
                          component='span'
                          sx={{
                            fontWeight: 700,
                            fontSize: '10px',
                            m: 0,
                            ml: growthRevenueVal !== 0 ? '-2px' : 0,
                          }}
                        >
                          {formatFinanceAmount(Math.abs(growthRevenueVal))}%
                        </Typography>
                      </Box>
                    )}
                  </div>
                </div>
                );
              })
              ) : null}
            </div>
          </CardContent>
        </Card>
      </Grid>
        </Grid>
      </Grid>

      {/* Job Tickets Modal */}
      <Dialog
        open={ticketModalOpen}
        onClose={handleCloseTicketModal}
        fullScreen={band === 'compact'}
        maxWidth={band === 'expanded' ? 'xl' : 'lg'}
        fullWidth={band !== 'compact'}
        PaperProps={{
          sx: {
            minHeight: band === 'compact' ? '100%' : band === 'expanded' ? '85vh' : '80vh',
            maxHeight: band === 'compact' ? '100%' : '90vh',
            display: 'flex',
            flexDirection: 'column',
            m: band === 'compact' ? 0 : undefined,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            flexDirection: band === 'compact' ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: band === 'compact' ? 'stretch' : 'center',
            gap: band === 'compact' ? 1 : 0,
            flexWrap: band === 'expanded' ? 'wrap' : 'nowrap',
            py: 0.75,
            px: 2,
            pr: band === 'compact' ? 5 : '48px',
            minHeight: 0,
            flexShrink: 0,
            borderBottom: 'none',
            boxShadow: 'none',
            position: 'relative',
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              flex: band === 'compact' ? 'none' : 1,
              textAlign: 'left',
              fontSize:
                band === 'compact' ? '1rem' : band === 'expanded' ? '2.85rem' : '2.5rem',
              lineHeight: 1.15,
              textDecoration: 'underline',
              cursor: 'pointer',
              wordBreak: 'break-word',
            }}
            onClick={() => {
              handleCloseTicketModal();
              history.push(`/jobs/ticket/${selectedJobData?.job_id}`);
            }}
          >
            {selectedJobData?.jobType === 'sub'
              ? `${selectedJobData?.companyNumberClon}-${selectedJobData?.job}`
              : `${selectedJobData?.companyNumber}-${selectedJobData?.job}`}
            <OriginalJobLabel job={selectedJobData} variant='report' />
          </Typography>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              flex: band === 'compact' ? 'none' : 1,
              textAlign: band === 'compact' ? 'left' : 'center',
              fontSize:
                band === 'compact' ? '0.95rem' : band === 'expanded' ? '2.25rem' : '2.5rem',
              lineHeight: 1.15,
              wordBreak: 'break-word',
            }}
          >
            {selectedJobData?.destination || 'N/A'}
          </Typography>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 600,
              flex: band === 'compact' ? 'none' : 1,
              textAlign: band === 'compact' ? 'left' : 'right',
              fontSize:
                band === 'compact' ? '0.9rem' : band === 'expanded' ? '2.25rem' : '2.5rem',
              lineHeight: 1.15,
              whiteSpace: band === 'compact' ? 'normal' : 'nowrap',
            }}
          >
            Total: {selectedJobData?.tickets?.reduce((sum, ticket) => sum + (Number(ticket.qty) || 0), 0).toFixed(2)} ({selectedJobData?.unit || 'TN'})
          </Typography>
          <IconButton
            onClick={handleCloseTicketModal}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers={false}
          sx={{
            padding: 0,
            borderTop: 'none',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {selectedJobData && (
            <TableContainer
              sx={{
                flex: 1,
                minHeight: 0,
                maxHeight: '100%',
                overflow: 'auto',
                '& thead .MuiTableCell-root': {
                  border: 'none !important',
                  boxShadow: 'none !important',
                },
                '& thead .MuiTableCell-stickyHeader': {
                  boxShadow: 'none !important',
                  border: 'none !important',
                },
              }}
            >
              <Table
                stickyHeader
                sx={{
                  borderCollapse: 'collapse',
                  borderSpacing: 0,
                  '& thead .MuiTableCell-root': {
                    border: 'none !important',
                    boxShadow: 'none !important',
                  },
                  '& thead .MuiTableCell-head': {
                    border: 'none !important',
                    boxShadow: 'none !important',
                  },
                  '& thead .MuiTableRow-root': {
                    border: 'none !important',
                  },
                  '& thead .MuiTableRow-root th': {
                    border: 'none !important',
                    boxShadow: 'none !important',
                  },
                }}
              >
                <TableHead
                  sx={{
                    border: 'none',
                    '& .MuiTableCell-root': {
                      border: 'none !important',
                      borderRight: 'none !important',
                      borderLeft: 'none !important',
                      borderTop: 'none !important',
                      borderBottom: 'none !important',
                      boxShadow: 'none !important',
                    },
                  }}
                >
                  <TableRow
                    sx={{
                      border: 'none !important',
                      '& th': {
                        border: 'none !important',
                        boxShadow: 'none !important',
                      },
                    }}
                  >
                    {TICKET_MODAL_COLUMNS.map(({ key, label }) => {
                      const active = ticketModalSort.key === key;
                      return (
                        <TableCell
                          key={key}
                          component="th"
                          scope="col"
                          onClick={() => handleTicketModalHeaderClick(key)}
                          sx={{
                            ...ticketModalTableHeaderCellSx,
                            cursor: 'pointer',
                            userSelect: 'none',
                            whiteSpace: 'nowrap',
                            '&:hover': { filter: 'brightness(0.97)' },
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              verticalAlign: 'middle',
                            }}
                          >
                            {label}
                            {active &&
                              (ticketModalSort.dir === 'asc' ? (
                                <NorthIcon sx={{ fontSize: '1rem', opacity: 0.9 }} />
                              ) : (
                                <SouthIcon sx={{ fontSize: '1rem', opacity: 0.9 }} />
                              ))}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedTicketModalRows.map((ticket, index) => (
                    <TableRow key={`${ticket?.ticket ?? ''}-${index}`} hover>
                      <TableCell>{ticket.ticket}</TableCell>
                      <TableCell>{Number(ticket.qty).toFixed(2)}</TableCell>
                      <TableCell>{ticket.driver}</TableCell>
                      <TableCell>{ticket.truck}</TableCell>
                      <TableCell>{ticket.scale}</TableCell>
                      <TableCell>{moment(ticket.dateTime).format('LLL')}</TableCell>
                      <TableCell>{moment(ticket.dateTimeUpdate).format('LLL')}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default NewDashboardView;
